import { useEffect, useRef, useState } from 'react';

import { PlayCircle, PauseCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  TextareaAutosize,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { IncomingCallsApi, MasterNameApi } from '@/api';
import { IcCall, IcClose, IcEdit, IcNoRecords } from '@/assets/images';
import { NarrowButton } from '@/components/Buttons';
import {
  DATE_TIME_FORMAT_WITH_SECONDS,
  TIME_FORMAT_WITH_SECONDS,
} from '@/config';
import { useFetchSasToken } from '@/hooks';
import IncomingCallCFSForm from '@/layouts/MainLayout/components/IncomingCall/components/IncomingCallCFSForm';
import { IncomingCallModalRoot } from '@/layouts/MainLayout/components/IncomingCall/styles';
import {
  ICall,
  IMasterNamePerson,
  IUser,
  MasterNameType,
  Role,
} from '@/models';
import { getAddressLocation, isDispatcherRole, isTargetRole } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import {
  formatPersonName,
  formatPhoneNumber,
  getFileUrlWithValidToken,
  getFormattedDateTime,
  openNewWindow,
} from '@/utils';

import { AudioPlayer, AudioPlayerHandle } from './AudioPlayer';
import { AppModal } from '../AppModal';

interface CallDetailsModalProps {
  open: boolean;
  onClose: () => void;
  call: ICall;
}

const TimelineDot = styled(Box)(({ theme }) => ({
  width: 1,
  backgroundColor: theme.palette.primary.main,
  position: 'relative',
  '::before': {
    position: 'absolute',
    content: '""',
    width: 7,
    height: 7,
    left: -3,
    top: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const customModalHeader = ({
  onClose,
  call,
}: {
  onClose: () => void;
  call: ICall;
}) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: '100%' }}
    >
      <Box flex={1} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography fontSize={16} fontWeight={500}>
          Call Details
        </Typography>
        {call?.cfs && (
          <>
            <ButtonBase
              onClick={() => {
                openNewWindow(`/cfs/${call?.cfs?._id}`, call?.cfs?._id);
              }}
            >
              <Typography fontSize={14} fontWeight={500} color="error">
                {call?.cfs.number}
              </Typography>
            </ButtonBase>
            <Typography fontSize={14} fontWeight={400}>
              {getAddressLocation(call?.cfs.address)}
            </Typography>
          </>
        )}
      </Box>
      <IconButton aria-label="close" onClick={onClose} className="btn-close">
        <IcClose />
      </IconButton>
    </Stack>
  );
};

const customModalCard = ({
  title,
  icon,
  content,
}: {
  title: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
}) => {
  const t = useTheme();
  return (
    <Box
      flex={1}
      border={1}
      borderColor={t.palette.divider}
      borderRadius={2}
      p={2}
    >
      <Typography
        display="flex"
        gap={1}
        pb={1}
        fontSize={14}
        fontWeight={500}
        alignItems="center"
      >
        {icon} {title}
      </Typography>
      <Divider />
      {content}
    </Box>
  );
};

const callDetailsInfoItem = ({
  label,
  value,
  showEdit,
  onEdit,
}: {
  label: string;
  value: string;
  showEdit?: boolean;
  onEdit?: () => void;
}) => {
  return (
    <Grid item xs={6}>
      <Typography
        fontSize={14}
        fontWeight={400}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {label}{' '}
        {showEdit && (
          <IconButton onClick={onEdit}>
            <IcEdit />
          </IconButton>
        )}
      </Typography>
      <Typography fontSize={14} fontWeight={500}>
        {value}
      </Typography>
    </Grid>
  );
};

const CallDetailTimelineItem = ({
  time,
  label,
  subLabel,
}: {
  time: string;
  label: string;
  subLabel?: string;
}) => {
  const theme = useTheme();
  return (
    <Stack direction="row" gap={2}>
      <TimelineDot></TimelineDot>
      <Box display="flex" alignItems="center">
        <Typography padding={0.5} fontWeight={700} fontSize={14}>
          {moment(time).format(TIME_FORMAT_WITH_SECONDS)}
        </Typography>
        <>
          <Typography padding={0.5} color={theme.palette.text.secondary}>
            •
          </Typography>
          <Typography
            color={theme.palette.text.secondary}
            padding={0.5}
            fontWeight={400}
            fontSize={14}
          >
            {label}
          </Typography>
          {subLabel && (
            <>
              <Typography padding={0.5} color={theme.palette.text.secondary}>
                •
              </Typography>
              <Typography
                color={theme.palette.text.secondary}
                padding={0.5}
                fontWeight={400}
                fontSize={14}
              >
                {subLabel}
              </Typography>
            </>
          )}
        </>
      </Box>
    </Stack>
  );
};

const CallDetailsModal = ({ open, onClose, call }: CallDetailsModalProps) => {
  const theme = useTheme();
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const isDispatcher = isDispatcherRole(user as IUser);
  const [notes, setNotes] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCFSForm, setShowCFSForm] = useState(false);
  const [masterNames, setMasterNames] = useState<IMasterNamePerson[]>([]);
  const [isLoadingCFSData, setIsLoadingCFSData] = useState(false);
  const audioPlayerRef = useRef<AudioPlayerHandle>(null);

  const sasToken = useFetchSasToken([call.file]);
  const fileUrl = call.file
    ? getFileUrlWithValidToken(call.file, sasToken)
    : undefined;

  let talkTime = 'N/A';
  if (
    typeof call?.answeredAt === 'string' &&
    typeof call?.completedAt === 'string'
  ) {
    const answered = new Date(call.answeredAt).getTime();
    const completed = new Date(call.completedAt).getTime();

    if (!isNaN(answered) && !isNaN(completed) && completed > answered) {
      const duration = Math.floor((completed - answered) / 1000);
      const minutes = Math.floor(duration / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (duration % 60).toString().padStart(2, '0');
      talkTime = `${minutes}m ${seconds}s`;
    }
  }

  let callAnsweredAt = '';
  if (typeof call.answeredAt === 'string') {
    callAnsweredAt = getFormattedDateTime(call.answeredAt);
  }

  const onEndCall = async () => {
    try {
      // Call API to end the call
      await IncomingCallsApi.endCall(call._id, {
        disposition: call.disposition || '',
        notes: call.notes || '',
      });
      onClose();
    } catch (error) {
      updateToast({
        open: true,
        message: 'Error ending call',
        type: ConfirmTypes.ERROR,
      });
    }
  };

  const updateNotes = async () => {
    try {
      // Call API to update notes
      await IncomingCallsApi.updateCallNotes(call._id, notes || '');
      call.notes = notes;
      updateToast({
        open: true,
        message: 'Notes updated successfully',
        type: ConfirmTypes.SUCCESS,
      });
    } catch (error) {
      updateToast({
        open: true,
        message: 'Error updating notes',
        type: ConfirmTypes.ERROR,
      });
    }
  };

  useEffect(() => {
    if (call.notes) {
      setNotes(call.notes);
    } else {
      setNotes('');
    }
  }, [call.notes]);

  const fetchCFSFormData = async () => {
    if (isLoadingCFSData) return;

    setIsLoadingCFSData(true);
    try {
      // Fetch master names based on phone number
      const masterNameParams = {
        search: call.phoneNumber,
        type: MasterNameType.PERSON,
        sort: JSON.stringify({ firstName: 1, lastName: 1 }),
      };
      const masterNamesResponse = await MasterNameApi.getMasterNames(
        masterNameParams,
      );

      setMasterNames(
        (masterNamesResponse.data as unknown as IMasterNamePerson[]) || [],
      );
    } catch (error) {
      updateToast({
        open: true,
        message: 'Error loading CFS form data',
        type: ConfirmTypes.ERROR,
      });
    } finally {
      setIsLoadingCFSData(false);
    }
  };

  const openCFSForm = async () => {
    await fetchCFSFormData();
    setShowCFSForm(true);
  };

  const onCloseCFSForm = () => {
    setShowCFSForm(false);
    onClose();
  };

  const togglePlayPause = () => {
    const audio = audioPlayerRef.current;
    if (!audio) return;

    audioPlayerRef.current.togglePlayback();
  };

  return (
    <>
      <AppModal
        open={open}
        customHeader={customModalHeader({ onClose, call })}
        classes={{ content: 'full-width' }}
        title="Call Details"
        onClose={() => {
          onClose();
        }}
      >
        <Stack gap={2}>
          <Stack flexDirection="row" gap={2} alignItems="stretch">
            {customModalCard({
              title: 'Call information',
              icon: <IcCall />,
              content: (
                <Grid container p={1} spacing={2}>
                  {callDetailsInfoItem({
                    label: 'Call ID',
                    value: call?.callId || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Date and Time',
                    value:
                      moment(call?.createdAt)
                        .local()
                        .format(DATE_TIME_FORMAT_WITH_SECONDS) || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Caller Name',
                    value: call?.callerName || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Caller number',
                    value: call?.phoneNumber || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Number Received on',
                    value: call?.receiverNumber || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Default Call ID',
                    value: call?.externalCallId || 'N/A',
                  })}
                  {!call.missed &&
                    callDetailsInfoItem({
                      label: 'Handled by',
                      value: formatPersonName(call?.handledBy) || '--',
                    })}
                  {callDetailsInfoItem({
                    label: 'Disposition',
                    value: call?.disposition || 'N/A',
                  })}
                  {callDetailsInfoItem({
                    label: 'Call Result',
                    value: call?.missed ? 'Missed' : 'Answered',
                  })}
                  {call?.extension &&
                    callDetailsInfoItem({
                      label: 'Extension',
                      value: call.extension,
                    })}
                </Grid>
              ),
            })}
            {customModalCard({
              title: 'Timeline',
              content: (
                <Box p={1}>
                  {CallDetailTimelineItem({
                    time: call?.startedAt || '',
                    label: 'Call received',
                  })}
                  {call.missed ? (
                    <CallDetailTimelineItem
                      time={call?.completedAt?.toLocaleString() || ''}
                      label="Call missed"
                    />
                  ) : (
                    call.answeredAt && (
                      <CallDetailTimelineItem
                        time={call?.answeredAt?.toLocaleString() || ''}
                        label={
                          call.handledBy
                            ? `Call answered by ${formatPersonName(
                                call.handledBy,
                              )} `
                            : 'Call answered'
                        }
                        subLabel={`Call answered in ${
                          call?.answeredAt
                            ? Math.floor(
                                (new Date(call?.answeredAt).getTime() -
                                  new Date(call?.createdAt ?? '').getTime()) /
                                  1000,
                              )
                            : 'N/A'
                        }s`}
                      />
                    )
                  )}
                  {call.completedAt && (
                    <CallDetailTimelineItem
                      time={call?.completedAt?.toLocaleString() || ''}
                      label="Call ended"
                      subLabel={
                        call?.missed ? undefined : `Talk time: ${talkTime}`
                      }
                    />
                  )}
                </Box>
              ),
            })}
          </Stack>
          <Stack flexDirection="row" gap={2} alignItems="stretch">
            {customModalCard({
              title: 'Notes',
              content: isDispatcher ? (
                <Stack>
                  <TextareaAutosize
                    minRows={3}
                    maxRows={5}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 4,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                    }}
                  ></TextareaAutosize>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      updateNotes();
                    }}
                    sx={{ mt: 2 }}
                  >
                    Save Notes
                  </Button>
                </Stack>
              ) : (
                <Typography p={1}>
                  {call?.notes || 'No notes available'}
                </Typography>
              ),
            })}
            {isTargetRole(user as IUser, Role.CALL_RECORDING_ACCESS) &&
              customModalCard({
                title: 'Recording',
                content: (
                  <>
                    <Stack
                      gap={2}
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        backgroundColor: theme.palette.divider,
                        borderRadius: 2,
                      }}
                      my={2}
                      py={4}
                    >
                      {call.file ? (
                        <Stack spacing={1} width="100%" alignItems="center">
                          <NarrowButton
                            className="btn-circle"
                            type="button"
                            onClick={togglePlayPause}
                          >
                            {isPlaying ? (
                              <PauseCircle sx={{ fontSize: '60px' }} />
                            ) : (
                              <PlayCircle sx={{ fontSize: '60px' }} />
                            )}
                          </NarrowButton>
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            sx={{ alignSelf: 'flex-center' }}
                          >
                            Call Recording
                          </Typography>
                          <Typography
                            fontSize={14}
                            fontWeight={400}
                            sx={{ alignSelf: 'flex-center' }}
                          >
                            {callAnsweredAt}
                          </Typography>
                        </Stack>
                      ) : (
                        <>
                          <IcNoRecords width={24} height={24} />
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            color={theme.palette.text.secondary}
                          >
                            Recording Not Available
                          </Typography>
                        </>
                      )}
                    </Stack>
                    {fileUrl && (
                      <AudioPlayer
                        src={fileUrl}
                        onPlaybackChanged={setIsPlaying}
                        ref={audioPlayerRef}
                      />
                    )}
                  </>
                ),
              })}
          </Stack>
          {!call.missed && (
            <Stack flexDirection="row" gap={2} justifyContent="flex-end">
              {!call.completedAt && (
                <Button variant="contained" color="error" onClick={onEndCall}>
                  Save and Close
                </Button>
              )}
              {!call?.cfs && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={openCFSForm}
                  disabled={isLoadingCFSData}
                >
                  {isLoadingCFSData ? 'Loading...' : 'Link CFS'}
                </Button>
              )}
            </Stack>
          )}

          {/* CFS Form Modal */}
          <IncomingCallModalRoot
            open={showCFSForm}
            title={
              call.externalCallId +
              ' ' +
              formatPhoneNumber(call.phoneNumber) +
              ' - ' +
              call.callId
            }
            onClose={onCloseCFSForm}
            hideCloseButton={false}
          >
            <>
              {showCFSForm && (
                <IncomingCallCFSForm
                  phoneNumber={call.phoneNumber || ''}
                  masterNames={masterNames}
                  incomingCallId={call._id}
                  onClose={onCloseCFSForm}
                />
              )}
            </>
          </IncomingCallModalRoot>
        </Stack>
      </AppModal>
    </>
  );
};

export default CallDetailsModal;
